
import { mapGetters, mapMutations, mapActions } from 'vuex'
import Breadcrumbs from './breadcrumbs.vue'
import Header from './header.vue'
import CarouselColorsFilter from './carouselColorsFilter.vue'
import Content from './plp-content'
import BackToTop from './backtotop.vue'
import Footer from './footer.vue'

export default {
  components: {
    Breadcrumbs,
    Header,
    CarouselColorsFilter,
    Content,
    BackToTop,
    Footer,
  },
  props: {
    refresh: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    pageHasLoaded: false,
    extraQueryParams: {},
    currencyChanged: false,
  }),
  computed: {
    ...mapGetters({
      vxSearchParameterString: 'search/getSearchParameterString',
      vxGetPlpScrollPosition: 'search/getPlpScrollPosition',
      vxIsReady: 'search/getIsReady',
      vxCurrency: 'currency/getCurrency',
    }),
    extraQueryParamsParsed() {
      const parsedStr =
        Object?.keys(this.extraQueryParams)
          ?.map((key) => `${key}=${this.extraQueryParams[key]}`)
          ?.join('&') || ''
      return parsedStr ? `&${parsedStr}` : ''
    },
    currencyLocale() {
      return `${this.vxCurrency.toLowerCase()}-${this.$i18n.locale}`
    },
  },
  watch: {
    vxSearchParameterString() {
      if (!this.vxIsReady) return
      this.$router.push(
        `${this.$route.path}?${this.vxSearchParameterString}${this.extraQueryParamsParsed}`
      )
    },
    async currencyLocale() {
      this.currencyChanged = true
      await this.init()
    },
  },
  async mounted() {
    await this.init()
  },
  beforeDestroy() {
    // UNBIND SCROLL EVENT
    window.removeEventListener('scroll', this.handleScroll)
    try {
      this.$store.commit('search/setIsReady', false)
    } catch (err) {
      this.$sentryException(err, {
        source: 'Searchspring search component',
        level: 'danger',
        extra: 'clear on destroy',
      })
    }
  },
  methods: {
    ...mapActions({
      vxQuerySearchspring: 'search/querySearchspring',
    }),
    ...mapMutations({
      vxSetSearchPage: 'search/setSearchPage',
      vxAddSearchFilter: 'search/addSearchFilter',
      vxResetSearchResults: 'search/resetSearchResults',
      vxSetSearchParameters: 'search/setSearchParameters',
      vxSetActivePriceFacet: 'search/setActivePriceFacet',
      vxSetPlpScrollPosition: 'search/setPlpScrollPosition',
    }),
    async init() {
      try {
        await this.$store.commit('search/setIsReady', false)
        if (this.refresh || this.currencyChanged) {
          await this.extractQueryParameters()
          await this.vxResetSearchResults()
          await this.vxQuerySearchspring()
        }
        this.pageHasLoaded = true
        await this.$store.commit('search/setIsReady', true)
      } catch (err) {
        this.$sentryException(err, {
          source: 'Searchspring search component',
          level: 'danger',
          extra: 'Failed to setup page',
        })
      }

      // BIND SCROLL EVENT
      window.addEventListener('scroll', this.handleScroll)
      if (this.vxGetPlpScrollPosition) {
        window.scrollTo(0, this.vxGetPlpScrollPosition)
      }
    },
    extractQueryParameters() {
      const urlQuery = this.$route.query
      const validKeyParts = ['page', 'q', 'sort', 'filter']
      if (!Object.keys(urlQuery).length > 0) return

      for (const key in urlQuery) {
        const value = urlQuery[key]
        if (validKeyParts.some((part) => key.includes(part))) {
          switch (key) {
            case 'page':
              this.vxSetSearchPage(parseInt(value))
              break
            case 'q':
              this.vxSetSearchParameters({
                query: value,
              })
              break
            case 'filter.ss_final_price.low':
            case 'filter.ss_final_price.high':
              this.setPriceFilterFromUrl(urlQuery)
              break
            default:
              if (key.includes('sort')) {
                this.vxSetSearchParameters({
                  sort: {
                    field: key.replace('sort.', ''),
                    direction: value,
                    label: '',
                  },
                })
              } else if (typeof value === 'string') {
                const castValue = value.split(',')
                castValue.forEach((val) => {
                  this.vxAddSearchFilter(`${key}=${val}`)
                })
              } else {
                value.forEach((val) => {
                  this.vxAddSearchFilter(`${key}=${val}`)
                })
              }
          }
        } else {
          this.extraQueryParams[key] = value
        }
      }
    },
    handleScroll() {
      this.vxSetPlpScrollPosition(window.scrollY)
    },
    setPriceFilterFromUrl(urlQuery) {
      const priceLow = parseInt(urlQuery['filter.ss_final_price.low'])
      const priceHigh = parseInt(urlQuery['filter.ss_final_price.high'])

      this.vxSetSearchParameters({
        prices: {
          low: priceLow,
          high: priceHigh,
        },
      })

      // CALCULATE ACTIVE PRICE RANGES
      const priceRanges = [
        [0, 50],
        [50, 100],
        [100, 150],
        [150, 300],
        [300, 500],
      ]

      const priceFacets = priceRanges.reduce((acc, range) => {
        if (priceLow <= range[0] && priceHigh >= range[1]) {
          acc.push(
            `filter.ss_final_price.low=${range[0]}&filter.ss_final_price.high=${range[1]}`
          )
        }
        return acc
      }, [])

      this.vxSetActivePriceFacet(priceFacets)
    },
  },
}
